import { ChevronRight } from "lucide-react";
import {
  EFFORT_LEVELS,
  type EffortLevel,
  getQuadrant,
  type ImpactLevel,
  LEVEL_LABELS,
  QUADRANT_BADGE_STYLES,
} from "@/bizminer/App/PillarInsights/ImprovementLevers/shared/improvementLeverUtils.ts";
import { Card } from "@/components/ui/card";
import type { BizminerImprovementLever } from "@/types/serializers";

interface RoadmapViewProps {
  improvementLevers: BizminerImprovementLever[];
  onSelect: (lever: BizminerImprovementLever) => void;
}

const impactOrder: Record<ImpactLevel, number> = { high: 0, medium: 1, low: 2 };

function groupByEffort(levers: BizminerImprovementLever[]) {
  const groups: Record<EffortLevel, BizminerImprovementLever[]> = {
    low: [],
    medium: [],
    high: [],
  };

  for (const lever of levers) {
    groups[lever.effort as EffortLevel].push(lever);
  }

  for (const effort of EFFORT_LEVELS) {
    groups[effort].sort((a, b) => {
      return (
        impactOrder[a.impact as ImpactLevel] -
        impactOrder[b.impact as ImpactLevel]
      );
    });
  }

  return groups;
}

interface RoadmapItemProps {
  lever: BizminerImprovementLever;
  onClick: () => void;
}

function RoadmapItem({ lever, onClick }: RoadmapItemProps) {
  const quadrant = getQuadrant(
    lever.impact as ImpactLevel,
    lever.effort as EffortLevel,
  );
  const badgeStyle = QUADRANT_BADGE_STYLES[quadrant];

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 bg-muted border border-border rounded-md text-left hover:border-foreground/20 hover:bg-muted/70 transition-[background-color,border-color]"
    >
      <div className="min-w-[72px] space-y-1">
        <div className="text-[0.625rem] text-muted-foreground uppercase tracking-wide">
          Impact
        </div>
        <span
          className={`inline-flex text-[0.625rem] font-semibold px-2 py-0.5 rounded-full uppercase ${badgeStyle}`}
        >
          {LEVEL_LABELS[lever.impact as ImpactLevel]}
        </span>
      </div>
      <span className="flex-1 text-sm text-foreground">{lever.title}</span>
      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
    </button>
  );
}

interface RoadmapColumnProps {
  title: string;
  subtitle: string;
  items: BizminerImprovementLever[];
  onSelect: (lever: BizminerImprovementLever) => void;
}

function RoadmapColumn({
  title,
  subtitle,
  items,
  onSelect,
}: RoadmapColumnProps) {
  return (
    <div className="border-r border-border last:border-r-0">
      <div className="px-4 pt-2.5 pb-2 bg-muted border-b border-border">
        <div className="font-semibold text-sm text-foreground">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
      <div className="p-3 space-y-2 min-h-[300px]">
        {items.map((lever) => (
          <RoadmapItem
            key={lever.id}
            lever={lever}
            onClick={() => onSelect(lever)}
          />
        ))}
      </div>
    </div>
  );
}

export default function RoadmapView({
  improvementLevers,
  onSelect,
}: RoadmapViewProps) {
  const grouped = groupByEffort(improvementLevers);

  return (
    <Card className="overflow-hidden">
      <div className="grid grid-cols-3">
        <RoadmapColumn
          title="Short Term"
          subtitle="Low Effort"
          items={grouped.low}
          onSelect={onSelect}
        />
        <RoadmapColumn
          title="Medium Term"
          subtitle="Medium Effort"
          items={grouped.medium}
          onSelect={onSelect}
        />
        <RoadmapColumn
          title="Long Term"
          subtitle="High Effort"
          items={grouped.high}
          onSelect={onSelect}
        />
      </div>
    </Card>
  );
}
